/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Form, Button, Stack } from 'react-bootstrap';
import { getSetting, setSetting } from '../../data';
import { MetadataContext } from '../MetadataContext';
import { SettingsContext } from '../SettingsContext';
import { useTheme } from 'styled-components';

function DisplaySettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const [prefix, setPrefix] = useState<string>('public');
    const [originalPrefix, setOriginalPrefix] = useState<string>('public');
    const [present, setPresent] = useState<string>('none');
    const [originalPresent, setOriginalPresent] = useState<string>('none');
    const [nativeMenu, setNativeMenu] = useState<boolean>(false);
    const [originalNativeMenu, setOriginalNativeMenu] = useState<boolean>(false);
    const [progressAutoRefresh, setProgressAutoRefresh] = useState<boolean>(true);
    const [originalProgressAutoRefresh, setOriginalProgressAutoRefresh] = useState<boolean>(true);
    const [fingerEditMode, setFingerEditMode] = useState<'legacy' | 'modern'>('legacy');
    const [originalFingerEditMode, setOriginalFingerEditMode] = useState<'legacy' | 'modern'>('legacy');
    const [refresh, setRefresh] = useState<number>(0);
    const metadataContext = useContext<any>(MetadataContext);
    const settingsContext = useContext(SettingsContext);
    const privateDirectory = String(metadataContext?.user_dir || '').trim() || 'user';

    const dirty = prefix !== originalPrefix ||
        present !== originalPresent ||
        nativeMenu !== originalNativeMenu ||
        progressAutoRefresh !== originalProgressAutoRefresh ||
        fingerEditMode !== originalFingerEditMode;

    useEffect(() => {
        settingsContext.setIsDirty(dirty);
    }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const rcPrefix = await getSetting('/prefix');
                setPrefix(rcPrefix || 'public');
                setOriginalPrefix(rcPrefix || 'public');

                const rcPresent = await getSetting('/present');
                const presentMode = (rcPresent === 'fill' || rcPresent === 'pause') ? rcPresent : 'none';
                setPresent(presentMode);
                setOriginalPresent(presentMode);

                const rcNativeMenu = await getSetting('/display/nativeMenu');
                const showNativeMenu = rcNativeMenu === 'true';
                setNativeMenu(showNativeMenu);
                setOriginalNativeMenu(showNativeMenu);

                const rcProgressAutoRefresh = await getSetting('/display/progressAutoRefresh').catch(() => 'true');
                const showProgressAutoRefresh = rcProgressAutoRefresh !== 'false';
                setProgressAutoRefresh(showProgressAutoRefresh);
                setOriginalProgressAutoRefresh(showProgressAutoRefresh);

                const rcFingerEditMode = await getSetting('/display/fingerEditMode').catch(() => 'legacy');
                const parsedFingerEditMode = rcFingerEditMode === 'modern' ? 'modern' : 'legacy';
                setFingerEditMode(parsedFingerEditMode);
                setOriginalFingerEditMode(parsedFingerEditMode);
            } catch (err: any) {
                console.error('Error loading display settings:', err);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        await setSetting('/prefix', prefix, 'Directory');
        await setSetting('/present', present, 'Presentation mode (none|fill|pause)');
        await setSetting('/display/nativeMenu', nativeMenu ? 'true' : 'false', 'Show native desktop menu bar (true|false)');
        await setSetting('/display/progressAutoRefresh', progressAutoRefresh ? 'true' : 'false', 'Auto-check S3 for progress data when opening dashboards (true|false)');
        await setSetting('/display/fingerEditMode', fingerEditMode, 'Fingerprint editor mode default (legacy|modern)');
        metadataContext.refresh();
        setRefresh(r => r + 1);
    }

    const changedStyle = (changed: boolean): React.CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem'
    } : {};

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Display</h3>
                    <div className="text-muted">Default directory and presentation behavior for dashboard views.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" onClick={() => {
                            setPrefix(originalPrefix);
                            setPresent(originalPresent);
                            setNativeMenu(originalNativeMenu);
                            setProgressAutoRefresh(originalProgressAutoRefresh);
                            setFingerEditMode(originalFingerEditMode);
                        }}>Clear</Button>
                        <Button variant="primary" onClick={save}>Save</Button>
                    </Stack>
                )}
            </div>
            <div className="mb-4" style={changedStyle(prefix !== originalPrefix)}>
                <Form.Label htmlFor="prefix" className="fw-semibold">Default Directory</Form.Label>
                <div className="text-muted small mb-2">Choose where file operations default when using cloud/local dialogs.</div>
                <Form.Select
                    id="prefix"
                    name="prefix"
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value)}
                    style={{ maxWidth: '28rem' }}
                >
                    <option value={privateDirectory}>{`Private (${privateDirectory})`}</option>
                    <option value="public">Public</option>
                    <option value="library/queue">Submission Queue</option>
                    <option value="library/reject">Rejected Submissions</option>
                    <option value="library/merged">Merged Submissions</option>
                </Form.Select>
            </div>
            <div className="mb-4" style={changedStyle(present !== originalPresent)}>
                <Form.Label htmlFor="present" className="fw-semibold">Presentation Mode</Form.Label>
                <div className="text-muted small mb-2">Controls whether dashboard values render immediately or animate with optional click-to-advance.</div>
                <Form.Select
                    id="present"
                    name="present"
                    value={present}
                    onChange={(e) => setPresent(e.target.value)}
                    style={{ maxWidth: '28rem' }}
                >
                    <option value="none">Immediate</option>
                    <option value="fill">Animate</option>
                    <option value="pause">Animate with pause for mouse click</option>
                </Form.Select>
            </div>
            <div className="mb-4" style={changedStyle(nativeMenu !== originalNativeMenu)}>
                <Form.Label htmlFor="nativeMenu" className="fw-semibold">Native Menu Bar</Form.Label>
                <div className="text-muted small mb-2">Show the desktop File/Edit menu bar on Windows/Linux. Requires app restart.</div>
                <Form.Check
                    id="nativeMenu"
                    name="nativeMenu"
                    type="switch"
                    label={nativeMenu ? 'Visible' : 'Hidden'}
                    checked={nativeMenu}
                    onChange={(e) => setNativeMenu(e.target.checked)}
                />
            </div>
            <div className="mb-4" style={changedStyle(progressAutoRefresh !== originalProgressAutoRefresh)}>
                <Form.Label htmlFor="progressAutoRefresh" className="fw-semibold">Progress Dashboard Auto-Refresh</Form.Label>
                <div className="text-muted small mb-2">Check S3 for updated progress data when opening Progress or Heatmap dashboards.</div>
                <Form.Check
                    id="progressAutoRefresh"
                    name="progressAutoRefresh"
                    type="switch"
                    label={progressAutoRefresh ? 'Enabled' : 'Disabled'}
                    checked={progressAutoRefresh}
                    onChange={(e) => setProgressAutoRefresh(e.target.checked)}
                />
            </div>
            <div className="mb-4" style={changedStyle(fingerEditMode !== originalFingerEditMode)}>
                <Form.Label htmlFor="fingerEditMode" className="fw-semibold">Fingerprint Editor Default</Form.Label>
                <div className="text-muted small mb-2">Controls whether the fingerprint page opens in the legacy form or the new modern editor by default.</div>
                <Form.Select
                    id="fingerEditMode"
                    name="fingerEditMode"
                    value={fingerEditMode}
                    onChange={(e) => setFingerEditMode(e.target.value === 'modern' ? 'modern' : 'legacy')}
                    style={{ maxWidth: '28rem' }}
                >
                    <option value="legacy">Legacy editor</option>
                    <option value="modern">Modern editor</option>
                </Form.Select>
            </div>
        </div>
    );
}

export default DisplaySettings;
